import { useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnmSupervisionType } from 'APIPackage/CatalogsModule/interfaces/IKnmSupervisionType';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { DragDropList, DragDropListItem, DragHandle } from 'UIPackage/MainModule/components/DragDropList';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { convertOrganisationToSelectOption } from 'PersonalAccountPackage/MainModule/common/convertOrganisationToSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './RequirementsForm.module.scss';
import { IFormRequirementsTemplateNpa } from '../interfaces/IFormRequirementsTemplateNpa';
import { IRequirementsForm } from '../interfaces/IRequirementsForm';
import { userRequirementsFormSubmit } from '../actions/userRequirementsAction';

const PAGE_SIZE = 50;

const convertSupervisionTypeToSelectOption = (
  supervisionType: IKnmSupervisionType,
): IKnmSupervisionTypeSelectOption => {
  const { id, name } = supervisionType;

  return {
    ...supervisionType,
    id,
    value: name,
    label: `${id} - ${name}`,
  };
};

interface IRequirementsFormProps {
  templateName?: string;
  organisations: IUserOrganisation[];
  activeOrganisationId?: string;
  supervisionTypes: IKnmSupervisionType[];
  activeSupervisionTypeId?: number;
  requirements: IFormRequirementsTemplateNpa[];
  isReset: boolean;
  onReset: () => void;
  onRequirementDelete?: (name: string, onUndo: () => void) => void;
}

interface IRequirementsFormStoreProps {
  isProsecutorOrSuperProsecutor: boolean | undefined;
}

const RequirementsFormWithoutStore = ({
  templateName = '',
  organisations,
  activeOrganisationId,
  supervisionTypes,
  activeSupervisionTypeId,
  requirements,
  isReset,
  onReset,
  onRequirementDelete,
  isProsecutorOrSuperProsecutor,
}: IRequirementsFormProps & IRequirementsFormStoreProps) => {
  const [npaPage, setNpaPage] = useState<number>(0);
  let resetForm: (() => void) | undefined;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    if (isReset) {
      if (resetForm) {
        resetForm();
      }
      onReset();
    }
  }, [isReset, onReset, resetForm]);

  useEffect(() => {
    if (resetForm) {
      resetForm();
    }
    setNpaPage(0);
  }, [templateName, resetForm]);

  const organisationsSelectOptions: Array<IUserOrganisationSelectOption> = organisations.map(
    convertOrganisationToSelectOption,
  );

  const organisationsSelectInitialValue = organisationsSelectOptions.find(option => option.id === activeOrganisationId);

  const supervisionTypesSelectOptions: Array<IKnmSupervisionTypeSelectOption> = supervisionTypes.map(
    convertSupervisionTypeToSelectOption,
  );

  const supervisionTypesSelectInitialValue = supervisionTypesSelectOptions.find(
    option => option.id === activeSupervisionTypeId,
  );

  const initialValues: IRequirementsForm = useMemo(
    () => ({
      templateName,
      organisation: organisationsSelectInitialValue,
      supervisionType: supervisionTypesSelectInitialValue,
      requirements,
    }),
    [organisationsSelectInitialValue, requirements, supervisionTypesSelectInitialValue, templateName],
  );

  const handleFormSubmit = useCallback(
    (values: IRequirementsForm) => {
      dispatch(userRequirementsFormSubmit(values, initialValues, resetForm));
    },
    [dispatch, initialValues, resetForm],
  );

  const handleSortEnd = (move: (from: number, to: number) => void) => ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    move(oldIndex, newIndex);
  };

  const lastVisibleNpaNumber = (npaPage + 1) * PAGE_SIZE;
  const isLoadButtonVisible = requirements.length > lastVisibleNpaNumber;

  const handleLoadButtonClick = useCallback(() => setNpaPage(prev => prev + 1), []);

  function renderForm({ handleSubmit, form }: FormRenderProps<IRequirementsForm>) {
    if (!resetForm && form && form.reset) {
      resetForm = () => {
        form.setConfig('keepDirtyOnReinitialize', false);
        form.reset();
        form.setConfig('keepDirtyOnReinitialize', true);
      };
    }

    const { push, update } = form.mutators;

    function addRequirement() {
      push('requirements', {
        newId: uuidv4(),
      });
    }

    const makeRemoveRequirementHandler = (index: number, data: IFormRequirementsTemplateNpa) => () => {
      const { id, newId, name } = data;
      const deleteId = id || id === 0 ? `${id}` : newId;

      update('requirements', index, {
        ...data,
        deleteId,
      });
      const undo = () => {
        const currentRequirementsValue = form.getFieldState('requirements')?.value;
        const undoIndex = currentRequirementsValue?.findIndex(
          (item: IFormRequirementsTemplateNpa) => item.deleteId === deleteId,
        );

        if (typeof undoIndex === 'number' && currentRequirementsValue?.[undoIndex]) {
          update(
            'requirements',
            undoIndex,
            deleteObjectProperties<IFormRequirementsTemplateNpa, keyof IFormRequirementsTemplateNpa>(
              currentRequirementsValue?.[undoIndex],
              ['deleteId'],
            ),
          );
        }
      };

      if (onRequirementDelete) {
        onRequirementDelete(name, undo);
      }
    };

    function handleCancelButtonClick() {
      form.setConfig('keepDirtyOnReinitialize', false);
      form.reset();
      form.setConfig('keepDirtyOnReinitialize', true);
    }

    return (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Наименование" placementLabel="left" color="black" labelSize="col-4">
            <TextInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="templateName"
              className="shared-row-field"
              placeholder="Наименование обязательного требования"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(512, 'Превышена допустимая длина строки'),
              )}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Организация" placementLabel="left" color="black" labelSize="col-4">
            <SelectInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="organisation"
              id="organisation"
              className="shared-row-field"
              options={organisationsSelectOptions}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label="Вид государственного контроля (надзора)"
            placementLabel="left"
            color="black"
            labelSize="col-4"
          >
            <SelectInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="supervisionType"
              id="supervisionType"
              className="shared-row-field"
              options={supervisionTypesSelectOptions}
              isSearchable
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <FieldBlock
          label="Требования"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={isProsecutorOrSuperProsecutor}
              onClick={addRequirement}
            />
          }
        ></FieldBlock>

        <FieldArray name="requirements" isEqual={equal}>
          {({ fields }) => (
            <DragDropList
              lockAxis="y"
              lockToContainerEdges
              lockOffset="0%"
              onSortEnd={handleSortEnd(fields.move)}
              useDragHandle
            >
              <ul className={styles.Requirements}>
                {fields.map((name, index) => {
                  const { id, newId, deleteId } = fields.value[index];

                  if ((deleteId || index > lastVisibleNpaNumber - 1) && (!newId || deleteId)) {
                    return null;
                  }
                  const fieldId = id || id === 0 ? `${id}` : newId;

                  return (
                    <DragDropListItem key={fieldId} index={index} disabled={isProsecutorOrSuperProsecutor}>
                      <Row notWrap>
                        <DragHandle>
                          <div className={styles.RequirementMove} hidden>
                            <IconMenu className={styles.RequirementMoveIcon} />
                          </div>
                        </DragHandle>
                        <Row>
                          <Row>
                            <FieldBlock>
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.props`}
                                placeholder="Формулировка обязательного требования"
                                resize="none"
                                autoHeight
                                validate={composeValidators(
                                  makeRequired('Поле не может быть пустым'),
                                  makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
                                )}
                              />
                            </FieldBlock>
                          </Row>
                          <Row>
                            <FieldBlock>
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.name`}
                                className={styles.FieldProps}
                                placeholder="Наименование НПА"
                                resize="none"
                                autoHeight
                                validate={composeValidators(
                                  makeRequired('Поле не может быть пустым'),
                                  makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
                                )}
                              />
                            </FieldBlock>
                          </Row>
                          <Row notWrap margin="none">
                            <FieldBlock margin="right">
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.number`}
                                placeholder="Номер НПА"
                                resize="none"
                                autoHeight
                                validate={composeValidators(
                                  makeRequired('Поле не может быть пустым'),
                                  makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                                )}
                              />
                            </FieldBlock>
                            <FieldBlock margin="right">
                              <DatePickerField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.date`}
                                id={`${name}.date`}
                                placeholder="Дата НПА"
                                isEqual={equal}
                                validate={makeRequired('Поле не может быть пустым')}
                              />
                            </FieldBlock>
                            <FieldBlock margin="right">
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.paragraph`}
                                placeholder="Пункт"
                                resize="none"
                                autoHeight
                                validate={makeMaxCharactersValidator(128, 'Превышена допустимая длина строки')}
                              />
                            </FieldBlock>
                            <FieldBlock margin="right">
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.part`}
                                placeholder="Часть"
                                resize="none"
                                autoHeight
                                validate={makeMaxCharactersValidator(128, 'Превышена допустимая длина строки')}
                              />
                            </FieldBlock>
                            <FieldBlock>
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.article`}
                                placeholder="Статья"
                                resize="none"
                                autoHeight
                                validate={makeMaxCharactersValidator(128, 'Превышена допустимая длина строки')}
                              />
                            </FieldBlock>
                          </Row>
                        </Row>

                        <input name={`${name}.id`} type="hidden" />
                        {!isProsecutorOrSuperProsecutor && (
                          <CloseButton
                            className={styles.RequirementDeleteButton}
                            onClick={makeRemoveRequirementHandler(index, fields.value[index])}
                          />
                        )}
                      </Row>
                    </DragDropListItem>
                  );
                })}
              </ul>
            </DragDropList>
          )}
        </FieldArray>
        {isLoadButtonVisible && (
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadButtonClick} />
        )}
        {!isProsecutorOrSuperProsecutor && (
          <div className="shared-action-buttons">
            <Button variant="contained" color="primary" type="submit" text="Сохранить" id="saveButton" />
            <Button
              className="shared-action-cancel"
              variant="contained"
              color="default"
              type="button"
              id="cancelButton"
              text="Отменить"
              onClick={handleCancelButtonClick}
            />
          </div>
        )}
      </form>
    );
  }

  return (
    <Form<IRequirementsForm>
      onSubmit={handleFormSubmit}
      render={renderForm}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
    />
  );
};

const mapStateToProps = (state: IRootState): IRequirementsFormStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
});

export const RequirementsForm = connect(mapStateToProps)(RequirementsFormWithoutStore);
